import React, { Fragment, useRef } from 'react';
import { TableCell, TableRow } from '@material-ui/core';
import PropTypes from 'prop-types';
import { DeleteIcon, EditIcon } from '../../Icons';
import ButtonIcon from '../IconButton';
import { getRowValue } from './cellRenderer';
import { useDrag, useDrop } from 'react-dnd';

const DraggableRow = props => {
  const {
    id,
    moveRow,
    index,
    row: { rawData, allowEdit, allowDelete, rowData },
    onDelete,
    onEdit,
    allowEditRow,
    allowDeleteRow,
    className,
    showMediaInPortal = false,
    displayImageInPortal,
  } = props;

  const ref = useRef(null);

  const handleEdit = () => {
    onEdit(rawData);
  };

  const handleDelete = () => {
    onDelete(rawData);
  };
  const handleDisplayImageInPortal = () => {
    displayImageInPortal(rawData);
  };

  const [{ handlerId }, drop] = useDrop({
    accept: 'row',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },

    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      // Time to actually perform the action
      moveRow(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'row',
    item: () => {
      return { id, index };
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  return (
    <TableRow
      className={className}
      ref={ref}
      style={{ opacity, cursor: 'move' }}
      data-handler-id={handlerId}
    >
      {Object.entries(rowData).map(([key, value]) => {
        return (
          <Fragment key={key}>
            <TableCell
              style={{ textAlign: '-webkit-center' }} //When we align using textAlign property,
              //align centers the text, -webkit-center will center the text and the image.
            >
              {getRowValue({
                data: value,
                showMediaInPortal,
                handleDisplayImageInPortal,
              })}
            </TableCell>
          </Fragment>
        );
      })}
      {(allowEditRow || allowDeleteRow) && (
        <TableCell>
          {allowEdit && (
            <ButtonIcon onClick={handleEdit} component={EditIcon} text="Edit" />
          )}
          {allowDelete && (
            <ButtonIcon
              component={DeleteIcon}
              onClick={handleDelete}
              text="Delete"
            />
          )}
        </TableCell>
      )}
    </TableRow>
  );
};

DraggableRow.propTypes = {
  row: PropTypes.object,
  collapisble: PropTypes.bool,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  allowEditRow: PropTypes.bool,
  allowDeleteRow: PropTypes.bool,
  subTable: PropTypes.object,
  className: PropTypes.string,
  showMediaInPortal: PropTypes.bool,
  displayImageInPortal: PropTypes.func,
  id: PropTypes.string,
  moveRow: PropTypes.func,
  index: PropTypes.number,
};

export default DraggableRow;
